/**
 * Slate-relative strength annotation for parlays.
 *
 * Purely informational: takes any array of parlay-shaped objects and returns
 * them with an added `strengthPercentile` field computed from a reference
 * distribution. Decoupled from `parlayConstructor` on purpose — it's a
 * presentation-layer concern that can be applied to optimizer output, tracked
 * parlay history, generated picks, or any other set of parlays, against any
 * reference distribution the caller provides.
 *
 * The default reference distribution is the input array's own scores, which
 * gives the intuitive "rank within this set" semantics. Callers who want
 * "rank within 30-day history" or "rank within today's generated slate" can
 * pass `referenceScores` explicitly.
 *
 * The input type constraint is minimal (just `legs: Array<{ score: number }>`)
 * so this annotator works on any parlay-shaped object regardless of which
 * layer produced it — server `ConstructedParlay`, API `OptimizedParlaySet`,
 * frontend mock data, tracked parlay records, etc.
 */

import { percentileRank } from './percentile';

/** Minimal parlay shape the annotator needs — just scored legs. */
export interface ParlayLike {
  legs: Array<{ score: number }>;
}

export interface StrengthOptions<T extends ParlayLike = ParlayLike> {
  /**
   * How to extract a comparable score from a parlay. Default: mean leg score.
   * Mean is preferred over sum so 2-leg and 3-leg parlays compete on per-leg
   * quality instead of parlay size (sum mechanically favors larger parlays).
   */
  scoreOf?: (parlay: T) => number;
  /**
   * External reference distribution. If omitted, the input array's own scores
   * are used — "rank within this set". Pass historical scores here to get
   * "rank within history" instead.
   */
  referenceScores?: number[];
  /**
   * Minimum set size for annotation to run. Below this, percentile ranks are
   * too coarse to be informative. Default: 3.
   */
  minSetSize?: number;
}

export type ParlayWithStrength<T extends ParlayLike> = T & {
  strengthPercentile?: number;
};

/** Default score extractor — mean leg score, normalized across parlay sizes. */
export function meanLegScore(parlay: ParlayLike): number {
  if (parlay.legs.length === 0) return 0;
  const sum = parlay.legs.reduce((acc, l) => acc + l.score, 0);
  return sum / parlay.legs.length;
}

/**
 * Return each parlay annotated with a slate-relative `strengthPercentile`.
 *
 * The annotation is a pure, read-only transform — it does not mutate the
 * input array or any parlay object. Callers receive a new array of objects
 * that structurally extend the input type with the optional field.
 *
 * When the input set is smaller than `minSetSize` (default 3) and no
 * `referenceScores` distribution is supplied, parlays are returned without
 * the field set — percentile ranks at that scale are not meaningful and the
 * UI should treat the field as absent.
 */
export function annotateStrengthPercentiles<T extends ParlayLike>(
  parlays: T[],
  options: StrengthOptions<T> = {},
): Array<ParlayWithStrength<T>> {
  const { scoreOf = meanLegScore, referenceScores, minSetSize = 3 } = options;

  if (parlays.length === 0) return [];
  if (parlays.length < minSetSize && (!referenceScores || referenceScores.length === 0)) {
    return parlays.map((p) => ({ ...p }));
  }

  const reference =
    referenceScores && referenceScores.length > 0 ? referenceScores : parlays.map(scoreOf);

  if (reference.length === 0) {
    return parlays.map((p) => ({ ...p }));
  }

  return parlays.map((p) => ({
    ...p,
    strengthPercentile: percentileRank(scoreOf(p), reference),
  }));
}
